import { Component, ElementRef, Input, OnDestroy, ViewChild, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AppWebBridgeService } from '@services/app-web-bridge-service';
import { BroadcastService } from '@services/broadcast-service';
import { ConnectionService } from '@services/connection-service';
import { CurrentComponentService } from '@services/current-component';
import { EventLoggerService } from '@services/event-logger-service';
import { FirebaseAndBranchEvent } from '@services/event-logger-service/event-logger.constant';
import { LocalStorageService } from '@services/local-storage-service';
import { WindowRefService } from '@services/window-ref-service';

import { AllocatedRegimen } from '../../../app-constant-types';
import { AppConfig } from '../../../app.config';

@Component({
  selector: 'user-explore',
  templateUrl: './user-explore.html',
  styleUrls: ['./user-explore.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class UserExploreComponent implements OnDestroy {
  isTabActive: boolean;
  @ViewChild('scrollContainer', { static: false }) scrollContainer: ElementRef;
  @Input('refresh')
  set refresh(isActive: boolean) {
    this.isTabActive = isActive;
    if (this.isTabActive) {
      if (this.scrollContainer) this.scrollContainer.nativeElement.scrollTop = 0;
    }
  }
  @Input('animation') animation: any;
  user: any;
  activeVideoElementIndex: number;
  userStatus: any = { PAID: false, GOT_REGIMEN: false, NO_PHOTO: false };
  username: string;
  feedById: any;
  feeds: any[] = [];
  feedsJSON: any[] = [];
  loading: boolean = true;
  unBoughtRegimen: AllocatedRegimen | undefined;
  latestIntersectedIndex: number;
  firstExecution: boolean = true;
  isExecuting: boolean = false;

  // Analytics tracking properties
  private static readonly MILLISECONDS_TO_SECONDS = 1000;
  private static readonly ANALYTICS_EVENTS = {
    EXIT: 'explore_page_exit',
    PAGE_OPEN: 'pageOpen'
  } as const;

  private static readonly ANALYTICS_PROPERTIES = {
    TIME_SPENT: 'time_spent_in_seconds',
    POSTS_VIEWED: 'posts_viewed',
    TOTAL_POSTS: 'total_posts'
  } as const;

  private readonly pageStartTime: number;
  private readonly postImpressions: Map<string, {
    position: number,
    type: string
  }> = new Map(); // objectId -> impression data
  private readonly viewedPosts: Set<number> = new Set(); // Track unique post views by index
  private intersectionObserver: IntersectionObserver;

  constructor(private router: Router,
    private conn: ConnectionService,
    private broadcast: BroadcastService,
    private window: WindowRefService,
    private route: ActivatedRoute,
    private eventLogger: EventLoggerService,
    private appConfig: AppConfig,
    private appWebBridge: AppWebBridgeService,
    private currentComponentService: CurrentComponentService,
    private changeDetector: ChangeDetectorRef,
    private readonly localStorageService: LocalStorageService) {
    this.currentComponentService.set(this);
    this.pageStartTime = Date.now();
  }

  async ngOnInit(): Promise<void> {
    this.appWebBridge.setStatusBarColor('black');
    this.user = await this.conn.getActingUser();
    if (this.user.isPaid() && this.user.get('orderState') === this.appConfig.Shared.User.OrderState.DELIVERED) this.userStatus.PAID = true;
    if (this.isExecuting) return;
    this.loading = true;
    this.isExecuting = true;
    await this.fetchFeed();
    this.loading = false;
    this.isExecuting = false;

    if (this.firstExecution) {
      const feedId = this.route.snapshot.queryParams.id;
      if (feedId) await this.prependFeed(feedId);
      this.firstExecution = false;
      this.loading = false;
      this.isExecuting = false;
    }
    this.changeDetector.detectChanges();
  }

  /**
   * Prepend the feed to the top of the feeds if `/explore/:id` is present
   */
  async prependFeed(feedId: string): Promise<void> {
    if (!feedId) return;
    const rawFeed = await this.conn.fetchExplorePostById(feedId);
    if (!rawFeed) return;

    // Serialize Parse object to plain object so template can access properties
    this.feedById = JSON.parse(JSON.stringify(rawFeed));

    // Transform if it's in new API format, otherwise use as-is
    let feedToPrepend = this.feedById;
    if (this.feedById.captionLanguageJson) {
      feedToPrepend = this.transformNewApiResponse([this.feedById])[0];
    }

    // Remove duplicate from existing feeds before prepending
    if (feedToPrepend.objectId) {
      this.feeds = this.feeds.filter((feed: any): boolean => {
        return feed.objectId !== feedToPrepend.objectId;
      });
    }

    this.feeds = [feedToPrepend, ...this.feeds];
    this.feedsJSON = [...this.feeds];
  }

  /**
   * Transform new API response format to match the old API format
   * @param newApiData - Array of feed objects from new API
   * @returns Transformed array compatible with existing UI
   */
  private transformNewApiResponse(newApiData: any[]): any[] {
    const currentLanguage = this.user.get('languagePreference') || this.appConfig.Shared.Languages.EN; // Default to English
    return newApiData.map((feedItem: any) => {
      const transformedItem: any = {
        objectId: feedItem.objectId,
        caption: feedItem.captionLanguageJson?.[currentLanguage] || feedItem.captionLanguageJson?.en || '',
        media: this.extractMediaForLanguage(feedItem.mediaLanguageJson, currentLanguage),
        ctaText: feedItem.ctaText,
        ctaUrl: feedItem.ctaUrl || '',
        likes: feedItem.likes || 0,
        targetGender: feedItem.targetGender || 'BOTH',
        userPaidState: feedItem.userPaidState || 'BOTH',
        active: feedItem.active !== false,
        classTag: feedItem.classTag || '',
        categoryTag: feedItem.categoryTag || '',
        createdAt: feedItem.createdAt,
        updatedAt: feedItem.updatedAt,
        rank: feedItem.rank || 0,
        type: feedItem.type || 'IMAGE',
        isVisible: false, // Initialize as not visible
        isLiked: false // Initialize as not liked, this might need to be fetched separately
      };

      // Only add branchLink if it exists and is not empty
      if (feedItem.branchLink && feedItem.branchLink.trim() !== '') {
        transformedItem.branchLink = feedItem.branchLink;
      }

      return transformedItem;
    });
  }

  /**
   * Extract media array for the current language from mediaLanguageJson
   * @param mediaLanguageJson - Media object with language keys
   * @param language - Current language code
   * @returns Array of media objects
   */
  private extractMediaForLanguage(mediaLanguageJson: any, language: string): any[] {
    if (!mediaLanguageJson) return [];

    // Try current language first, fallback to English, then any available language
    const mediaArray = mediaLanguageJson[language] ||
                      mediaLanguageJson.en ||
                      Object.values(mediaLanguageJson)[0] ||
                      [];

    return Array.isArray(mediaArray) ? mediaArray : [];
  }

  async fetchFeed(): Promise<any> {
    try {
      // Use the new connection service method to get feeds
      const rawFeeds = await this.conn.fetchSuitableFeedsForUser();
        // This is new API format, transform it
        this.feeds = this.transformNewApiResponse(rawFeeds?.data);

      // Set all feeds directly without throttling
      this.feedsJSON = this.feeds;
    } catch (error) {
      this.feeds = [];
      this.feedsJSON = [];
    }
    this.setElementIntersectionObservers();
    this.eventLogger.cleverTapEvent(UserExploreComponent.ANALYTICS_EVENTS.PAGE_OPEN, JSON.stringify({ pageName: 'explore' }));
    const regimens = await this.conn.fetchRegimens();
    this.unBoughtRegimen = regimens.find((each: AllocatedRegimen): boolean => !each.active && !each.orderPlaced);
  }



  async onCTAClick(feed: any): Promise<void> {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: `explore-${this.getEventFormattedString(feed.ctaText)}` }));
    this.eventLogger.trackEvent('feed_cta_click', { url: feed.ctaUrl, feed_id: feed.objectId });
    await this.conn.navigateToURL(feed.ctaUrl);
  }

  // Input -> Check my regimen
  // Output -> check-my-regimen
  getEventFormattedString(eventName: string): string {
    const formattedString = eventName.split(' ').join('-').toLowerCase();
    return formattedString;
  }

  async updateIsLikedFromFeedComponent(liked: boolean, index: number): Promise<void> {
    const feed = this.feeds[index];
    const prevLikedValue = feed?.isLiked;
    feed.isLiked = liked;
    try {
      if (feed.isLiked !== prevLikedValue) {
        const { result } = await this.conn.likeFeed(feed?.objectId || feed?.id);
        if (result === 'success') {
          await this.updateLocalFeedData(feed, { isLiked: true });
        }
      }
      // this.feedsJSON[index] = JSON.parse(JSON.stringify(feed));
      this.eventLogger.trackEvent('feed_like', { liked: this.feedsJSON[index].liked, feed_id: feed.objectId });
    } catch (error) {
      feed.isLiked = prevLikedValue;
      this.feedsJSON[index] = JSON.parse(JSON.stringify(feed));
      this.broadcast.broadcast('NOTIFY', { message: error.toString() });
    }
  }

  async updateLocalFeedData(feed: any, data: Record<string, any>): Promise<void> {
    const localFeeds = this.localStorageService.getValue('explorePosts');
    if (!localFeeds) return;
    const parsedLocalFeeds = await Promise.resolve(JSON.parse(localFeeds));
    const feedIndex = parsedLocalFeeds?.findIndex((_feed) => _feed.objectId === feed.objectId);
    if (feedIndex !== -1) {
      const updatedFeed = { ...parsedLocalFeeds[feedIndex], ...data };
      parsedLocalFeeds[feedIndex] = updatedFeed;
      this.localStorageService.setValue('explorePosts', JSON.stringify(parsedLocalFeeds));
    }
  }

  buyRegimen(): void {
    this.eventLogger.cleverTapEvent('click', JSON.stringify({ name: 'explore-buy-regimen' }));
    this.eventLogger.trackEventForClass(FirebaseAndBranchEvent.BUY_NOW_CLICKED, this.unBoughtRegimen.class);
    this.eventLogger.trackInFirebaseAndBranchForClass(FirebaseAndBranchEvent.BUY_NOW_CLICKED, this.unBoughtRegimen.class);
    this.conn.navigateToURL(`/user?tab=regimen&class=${this.unBoughtRegimen.class}`);
  }

  /**
   * Creates IntersectionObserver to the scroll container and makes all the children of container to observe for the callback.
   * Callback is called when any child's visibility reaches 0.3(30%) or 0.75(75%) within the scroll container viewport.
   */
  private initializeScrollContainer(limit: number = 15): boolean {
    if (!this.scrollContainer?.nativeElement && limit > 0) {
      // scrollContainer not available yet, wait and try again
      setTimeout((): void => this.setElementIntersectionObservers(limit - 1), 100);
      return false;
    }
    return true;
  }

  private createIntersectionObserver(): void {
    if (!this.intersectionObserver) {
      const options = {
        root: this.scrollContainer?.nativeElement,
        rootMargin: '0px',
        threshold: [0.3, 0.75],
      };
      this.intersectionObserver = new IntersectionObserver(this.elementIntersectionCallback, options);
    }
  }

  private observePostElements(): void {
    this.feedsJSON.forEach((each: any, index: number): void => {
      const element = this.window.nativeWindow.document.getElementById(`${index}-feed`);
      if (element) {
        // Check if element is already being observed
        const existingEntry = Array.from(this.intersectionObserver.takeRecords())
          .find(entry => entry.target === element);

        if (!existingEntry) {
          this.intersectionObserver.observe(element);
        }
      }
    });
  }

  setElementIntersectionObservers(limit: number = 15): void {
    if (!this.initializeScrollContainer(limit)) return;

    this.createIntersectionObserver();
    this.observePostElements();
  }

  private handlePostVisibility(index: number, isVisible: boolean): void {
    this.feedsJSON[index].isVisible = isVisible;

    if (isVisible) {
      const currentPost = this.feedsJSON[index];
      this.trackPostView(index, currentPost);

      // Send impression event immediately when post becomes visible
      if (!this.postImpressions.has(currentPost.objectId)) {
        this.postImpressions.set(currentPost.objectId, {
          position: index,
          type: currentPost.type
        });

        // Send the impression event
        this.eventLogger.cleverTapEvent('impression', JSON.stringify({
          position: index,
          id: currentPost.objectId,
          type: 'feed',
          name: 'explore_post_impression',
        }));
      }
    }
  }

  private trackPostView(index: number, currentPost: any): void {
    if (!this.viewedPosts.has(index)) {
      this.viewedPosts.add(index);
    }
  }

  private handleVideoVisibility(index: number, isVisible: boolean): void {
    if (this.activeVideoElementIndex >= 0
        && this.activeVideoElementIndex !== index
        && isVisible) {
      this.makeVisibilityOfActiveVideoToFalse();
    }
  }

  elementIntersectionCallback = (intersectionArray: any): void => {
    const MIN_THRESHOLD = 0.5;
    if (!intersectionArray.length || !this.feedsJSON.length) return;

    const intersectionObj = intersectionArray[0];
    const { id }: { id: string } = intersectionObj.target;
    const index = Number(id.split('-feed')[0]);

    if (this.latestIntersectedIndex === index) return;
    this.latestIntersectedIndex = index;
    if (!this.feedsJSON[index]) return;

    const isNowVisible = intersectionObj.intersectionRatio > MIN_THRESHOLD;

    this.handlePostVisibility(index, isNowVisible);
    this.handleVideoVisibility(index, isNowVisible);
  };

  /**
   * We are forcing the isVisible of active video to false, even if its false before. in order to pause the video.
   */
  makeVisibilityOfActiveVideoToFalse(): void {
    if (!this.feedsJSON[this.activeVideoElementIndex].isVisible) this.feedsJSON[this.activeVideoElementIndex].isVisible = true;
    setTimeout((feed_: any): void => {
      const feed = feed_;
      feed.isVisible = false;
      delete this.activeVideoElementIndex;
    }, 0, this.feedsJSON[this.activeVideoElementIndex]);
  }

  /**
   * Makes the current 'activeVideoElementIndex' feeds visibility to false.
   * And Stores the index of active video in 'activeVideoElementIndex' variable.
   * @param {number} index - index of active video which is playing.
   */
  onVideoPlay(index: number): void {
    this.eventLogger.trackEvent('feed_video_played', {
      liked: this.feedsJSON[index].liked,
      feed_id: this.feedsJSON[index].objectId,
    });
    if (this.activeVideoElementIndex >= 0 && this.activeVideoElementIndex !== index) {
      this.makeVisibilityOfActiveVideoToFalse();
    } else this.activeVideoElementIndex = index;
  }

  trackById(index: number): number {
    return index;
  }

  back(): void {
    this.broadcast.broadcast('NAVIGATION_BACK');
  }

  stopPropagation(event: any): void {
    event.stopPropagation();
  }

  private getSessionDuration(): number {
    return Math.round((Date.now() - this.pageStartTime) / UserExploreComponent.MILLISECONDS_TO_SECONDS);
  }

  private sendExitAnalytics(): void {
    const {
      TIME_SPENT,
      POSTS_VIEWED,
      TOTAL_POSTS
    } = UserExploreComponent.ANALYTICS_PROPERTIES;

    const exitEventData = {
      [TIME_SPENT]: this.getSessionDuration(),
      [POSTS_VIEWED]: this.viewedPosts.size,
      [TOTAL_POSTS]: this.feedsJSON.length
    };

    this.eventLogger.cleverTapEvent(
      UserExploreComponent.ANALYTICS_EVENTS.EXIT,
      JSON.stringify(exitEventData)
    );
  }

  private cleanup(): void {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
    this.conn.updateLastActiveTime('EXPLORE_TAB');
    this.currentComponentService.remove(this);
  }

  ngOnDestroy(): void {
    this.cleanup();
    this.sendExitAnalytics();
  }
}
